import { useState } from 'react';

const vfxImages = [
  {
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
    effect: 'Lightning',
    filterStyle: { filter: 'brightness(1.25) contrast(1.1) saturate(1.4)', boxShadow: 'inset 0 0 60px rgba(147, 197, 253, 0.5)' }
  },
  {
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
    effect: 'Fire',
    filterStyle: { filter: 'brightness(1.2) contrast(1.1) saturate(1.5) sepia(0.2)', boxShadow: 'inset 0 0 60px rgba(251, 146, 60, 0.5)' }
  },
  {
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
    effect: 'Particles',
    filterStyle: { filter: 'brightness(1.25) contrast(1.1) saturate(1.4)', boxShadow: 'inset 0 0 70px rgba(192, 132, 252, 0.45)' }
  },
  {
    image: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=400&q=80',
    effect: 'Glow',
    filterStyle: { filter: 'brightness(1.3) contrast(1.05) saturate(1.3)', boxShadow: 'inset 0 0 80px rgba(250, 204, 21, 0.4)' }
  },
  {
    image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80',
    effect: 'Cinematic',
    filterStyle: { filter: 'brightness(1.15) contrast(1.15) saturate(1.3)', boxShadow: 'inset 0 0 60px rgba(59, 130, 246, 0.4)' }
  },
  {
    image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=400&q=80',
    effect: 'Explosion',
    filterStyle: { filter: 'brightness(1.25) contrast(1.15) saturate(1.6)', boxShadow: 'inset 0 0 70px rgba(239, 68, 68, 0.5)' }
  },
];

const VFXSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % vfxImages.length);
      setIsGenerating(false);
    }, 1500);
  };

  const current = vfxImages[currentIndex];

  return (
    <div id="vfx-section" className="mb-16 sm:mb-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
        <div className="order-2 lg:order-1">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-primary/20">
              <span className="material-symbols-outlined text-primary text-xl sm:text-2xl">auto_fix_high</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white">AI Visual Effects</h3>
          </div>
          <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-lg">
            Add Hollywood-quality visual effects to your footage. Create stunning effects without expensive software.
          </p>
          <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
            <li className="flex items-center gap-2 sm:gap-3 text-white/60 text-sm">
              <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
              Real-time particle effects
            </li>
            <li className="flex items-center gap-2 sm:gap-3 text-white/60 text-sm">
              <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
              AI-powered rotoscoping
            </li>
            <li className="flex items-center gap-2 sm:gap-3 text-white/60 text-sm">
              <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
              Seamless compositing
            </li>
          </ul>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn-glow flex h-10 sm:h-12 items-center justify-center rounded-full bg-primary px-6 sm:px-8 text-xs sm:text-sm font-bold text-background-dark disabled:opacity-50"
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>
        <div className="order-1 lg:order-2">
          <div className="glass-panel rounded-xl sm:rounded-[2rem] p-4 sm:p-6 relative overflow-hidden">
            {isGenerating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-20 rounded-xl sm:rounded-[2rem]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-3 sm:mb-4"></div>
                <p className="text-white/70 text-xs sm:text-sm">Applying effects...</p>
              </div>
            )}
            <div className={`grid grid-cols-2 gap-3 sm:gap-4 transition-opacity duration-300 ${isGenerating ? 'opacity-30' : 'opacity-100'}`}>
              <div className="aspect-square rounded-lg sm:rounded-xl overflow-hidden bg-white/5 relative">
                <img src={current.image} alt="Before VFX" className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute bottom-1 sm:bottom-2 left-1 sm:left-2 text-[10px] sm:text-xs bg-black/60 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-white/80">Before</span>
              </div>
              <div className="aspect-square rounded-lg sm:rounded-xl overflow-hidden bg-white/5 relative">
                <img src={current.image} alt="After VFX" className="w-full h-full object-cover" style={current.filterStyle} loading="lazy" />
                <span className="absolute bottom-1 sm:bottom-2 left-1 sm:left-2 text-[10px] sm:text-xs bg-primary/80 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-background-dark font-bold">After</span>
              </div>
            </div>
            <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
              <span className="px-2 sm:px-3 py-1 rounded-full bg-primary/20 text-[10px] sm:text-xs text-primary">{current.effect}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VFXSection;
